import {
  AnimatePresence,
  motion,
  type PanInfo,
  useDragControls,
  useReducedMotion,
} from 'framer-motion'
import { ArrowLeft, X } from 'lucide-react'
import {
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import {
  iosBackdropTransition,
  iosSheetSpring,
  isNativeIOS,
  webSurfaceTransition,
} from '../../lib/iosMotion'
import { haptic } from '../../lib/native'
import {
  useOverlayFocusReveal,
  useOverlayScrollLock,
} from '../../hooks/useOverlayBehavior'
import { VoxnelaMark } from './VoxnelaMark'
import { useUiText } from '../../i18n/ui'

export function SheetLoadingState({
  label,
  sublabel,
  children,
  className = '',
}: PropsWithChildren<{
  label?: ReactNode
  sublabel?: ReactNode
  className?: string
}>) {
  const { text } = useUiText()
  return (
    <div
      className={`float-island-sheet-loading-center${className ? ` ${className}` : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className="sheet-loading-logo-box" aria-hidden="true">
        <VoxnelaMark animated />
      </div>
      <div className="sheet-loading-text-group">
        <span className="sheet-loading-action-label">{label ?? text('loading')}</span>
        {sublabel && <span className="sheet-loading-action-sublabel">{sublabel}</span>}
        {children}
      </div>
    </div>
  )
}

export function FloatIslandSheet({
  open,
  onClose,
  onBack,
  backLabel = 'Geri',
  title,
  subtitle,
  icon,
  headerActions,
  headerLayout = 'standard',
  showCloseButton = true,
  loading = false,
  loadingLabel,
  children,
  className,
  style,
  keyboardAware = true,
  dismissible = true,
}: PropsWithChildren<{
  open: boolean
  onClose: () => void
  onBack?: () => void
  backLabel?: string
  title: ReactNode
  subtitle?: ReactNode
  icon?: ReactNode
  headerActions?: ReactNode
  headerLayout?: 'standard' | 'border-toolbar'
  showCloseButton?: boolean
  loading?: boolean
  loadingLabel?: string
  className?: string
  style?: CSSProperties
  keyboardAware?: boolean
  dismissible?: boolean
}>) {
  const { text } = useUiText()
  const headingId = useId()
  const dragControls = useDragControls()
  const reduceMotion = useReducedMotion()
  const nativeIOS = isNativeIOS()
  const didDragRef = useRef(false)
  const panelRef = useRef<HTMLElement>(null)
  const [expanded, setExpanded] = useState(false)

  useOverlayScrollLock(open)
  useOverlayFocusReveal(open, panelRef, keyboardAware)

  useEffect(() => {
    if (open) void haptic('selection')
  }, [open])

  useEffect(() => {
    if (!open) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) {
        setExpanded(false)
        onClose()
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [dismissible, onClose, open])

  const settleSheet = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    const draggedDown = info.offset.y > 112 || info.velocity.y > 820
    const draggedUp = info.offset.y < -48 || info.velocity.y < -480

    if (draggedDown && !dismissible) {
      void haptic('soft')
      return
    }
    if (draggedDown && (!expanded || info.offset.y > 180)) {
      setExpanded(false)
      void haptic('soft')
      onClose()
      return
    }
    if (draggedUp) {
      setExpanded(true)
      void haptic('selection')
      return
    }
    if (expanded && (info.offset.y > 48 || info.velocity.y > 420)) {
      setExpanded(false)
      void haptic('selection')
    }
  }

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence onExitComplete={() => setExpanded(false)}>
      {open && (
        <motion.div
          className={`float-island-sheet-root fortale-floatisland-sheet-root${keyboardAware ? ' keyboard-aware' : ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={iosBackdropTransition}
          onMouseDown={(event) => {
            if (dismissible && event.target === event.currentTarget) {
              setExpanded(false)
              onClose()
            }
          }}
        >
          <motion.section
            ref={panelRef}
            className={`float-island-sheet fortale-floatisland-sheet-panel fortale-sheet-surface float-island-sheet-header-${headerLayout}${expanded ? ' is-expanded' : ''}${className ? ` ${className}` : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={headingId}
            initial={
              reduceMotion
                ? { opacity: 0 }
                : nativeIOS
                  ? { y: 62, opacity: 0, scale: 0.985 }
                  : { y: 72, opacity: 0, scale: 0.985 }
            }
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={
              reduceMotion
                ? { opacity: 0 }
                : nativeIOS
                  ? { y: 74, opacity: 0, scale: 0.985 }
                  : { y: 82, opacity: 0, scale: 0.985 }
            }
            transition={
              reduceMotion
                ? { duration: 0.12 }
                : nativeIOS
                  ? iosSheetSpring
                  : webSurfaceTransition
            }
            style={style}
            drag={nativeIOS && dismissible ? 'y' : false}
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: expanded ? -28 : -220, bottom: 560 }}
            dragElastic={{ top: 0.14, bottom: 0.24 }}
            dragMomentum={false}
            dragSnapToOrigin
            onDrag={(_event, info) => {
              if (Math.abs(info.offset.y) > 6) didDragRef.current = true
            }}
            onDragEnd={settleSheet}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {headerLayout === 'standard' ? (
              <>
                <button
                  className="float-island-sheet-handle fortale-sheet-handle"
                  type="button"
                  aria-label={
                    expanded ? text('sheetCollapse') : text('sheetExpand')
                  }
                  aria-expanded={expanded}
                  onPointerDown={(event) => {
                    didDragRef.current = false
                    if (nativeIOS && dismissible) {
                      dragControls.start(event.nativeEvent, { snapToCursor: false })
                    }
                  }}
                  onClick={() => {
                    if (!didDragRef.current) setExpanded((current) => !current)
                    didDragRef.current = false
                  }}
                />
                <header className="float-island-sheet-header fortale-sheet-header">
                  <div className="float-island-sheet-heading">
                    {onBack ? (
                      <button
                        className="float-island-sheet-back"
                        type="button"
                        aria-label={backLabel}
                        onClick={onBack}
                      >
                        <ArrowLeft size={19} />
                      </button>
                    ) : (
                      icon && (
                        <span className="float-island-sheet-icon" aria-hidden="true">
                          {icon}
                        </span>
                      )
                    )}
                    <span>
                      <strong id={headingId}>{title}</strong>
                      {subtitle && <small>{subtitle}</small>}
                    </span>
                  </div>
                  <div className="float-island-sheet-actions">
                    {headerActions}
                    {showCloseButton && (
                      <button
                        className="app-close-button float-island-sheet-close"
                        type="button"
                        aria-label={dismissible ? text('close') : text('cannotClose')}
                        disabled={!dismissible}
                        onClick={() => {
                          setExpanded(false)
                          onClose()
                        }}
                      >
                        <X size={19} />
                      </button>
                    )}
                  </div>
                </header>
              </>
            ) : (
              <header className="float-island-sheet-border-toolbar">
                <strong id={headingId} className="visually-hidden">
                  {title}
                </strong>
                <span
                  className="float-island-sheet-border-line float-island-sheet-border-line-leading"
                  aria-hidden="true"
                />
                <span className="float-island-sheet-border-back-slot">
                  {onBack && (
                    <button
                      className="float-island-sheet-border-back"
                      type="button"
                      aria-label={backLabel}
                      onClick={onBack}
                    >
                      <ArrowLeft size={19} />
                    </button>
                  )}
                </span>
                <span
                  className="float-island-sheet-border-line float-island-sheet-border-line-middle"
                  aria-hidden="true"
                />
                <div className="float-island-sheet-border-actions">
                  {headerActions}
                </div>
                <span
                  className="float-island-sheet-border-line float-island-sheet-border-line-trailing"
                  aria-hidden="true"
                />
                <span className="float-island-sheet-border-close-slot">
                  {showCloseButton && (
                    <button
                      className="app-close-button float-island-sheet-close"
                      type="button"
                      aria-label={dismissible ? text('close') : text('cannotClose')}
                      disabled={!dismissible}
                      onClick={() => {
                        setExpanded(false)
                        onClose()
                      }}
                    >
                      <X size={19} />
                    </button>
                  )}
                </span>
                <span
                  className="float-island-sheet-border-line float-island-sheet-border-line-final"
                  aria-hidden="true"
                />
              </header>
            )}
            <div className="float-island-sheet-body fortale-sheet-body">
              {loading ? (
                <SheetLoadingState label={loadingLabel} />
              ) : (
                children
              )}
            </div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
